// src/components/LargeTileProcessing.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import './LargeTileProcessing.css';

const LargeTileProcessing = ({ 
  isOpen, 
  onClose, 
  applyAllCorrections,
  selectedModel = 'picogram2'
}) => {
  // State management
  const [tiffFile, setTiffFile] = useState(null);
  const [tiffUrl, setTiffUrl] = useState(null);
  const [tiffMetadata, setTiffMetadata] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  
  // Local color corrections state
  const [colorCorrections, setColorCorrections] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
    gamma: 1,
    redEnhancement: 0,
    blueReduction: 0,
    greenBalance: 0,
    depthCompensation: 0,
    turbidityReduction: 0,
    particleReduction: 0,
    colorTemperature: 0,
    tint: 0,
    clarity: 0,
    dehaze: 0
  });
  
  const [transformations, setTransformations] = useState({
    rotation: 0,
    scale: 1,
    flipHorizontal: false,
    flipVertical: false
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState('basic');
  
  // Tiling state
  const [tiles, setTiles] = useState([]);
  const [selectedTileIndex, setSelectedTileIndex] = useState(0);
  const [tileSize] = useState({ width: 800, height: 800 });
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processedTiles, setProcessedTiles] = useState([]);
  
  // Preview state
  const [previewCanvas, setPreviewCanvas] = useState(null);
  const previewCanvasRef = useRef(null);
  
  // Final mask state
  const [finalMask, setFinalMask] = useState(null);
  
  const fileInputRef = useRef(null);

  // Calculate tiles for the image
  const calculateTiles = useCallback((width, height) => {
    const tilesX = Math.ceil(width / tileSize.width);
    const tilesY = Math.ceil(height / tileSize.height);
    const calculatedTiles = [];
    
    for (let y = 0; y < tilesY; y++) {
      for (let x = 0; x < tilesX; x++) {
        const tileX = x * tileSize.width;
        const tileY = y * tileSize.height;
        const tileWidth = Math.min(tileSize.width, width - tileX);
        const tileHeight = Math.min(tileSize.height, height - tileY);
        
        calculatedTiles.push({
          id: `${x}_${y}`,
          x: tileX,
          y: tileY,
          width: tileWidth,
          height: tileHeight,
          gridX: x,
          gridY: y,
          processed: false
        });
      }
    }
    
    return calculatedTiles;
  }, [tileSize]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file type
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const isTiff = file.type === 'image/tiff' || 
                  file.type === 'image/tif' || 
                  fileExtension === 'tiff' || 
                  fileExtension === 'tif';
    
    setTiffFile(file);
    
    try {
      let imageData;
      
      if (isTiff) {
        // Process TIFF file
        imageData = await processTiffFile(file);
      } else {
        // Process PNG/JPG files
        const url = URL.createObjectURL(file);
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
        
        imageData = {
          url: url,
          metadata: {
            width: img.naturalWidth,
            height: img.naturalHeight,
            fileInfo: {
              name: file.name,
              size: file.size,
              type: file.type
            }
          }
        };
      }
      
      setTiffUrl(imageData.url);
      setTiffMetadata(imageData.metadata);
      setImageDimensions({
        width: imageData.metadata.width,
        height: imageData.metadata.height
      });
      
      // Calculate tiles
      const calculatedTiles = calculateTiles(imageData.metadata.width, imageData.metadata.height);
      setTiles(calculatedTiles);
      
      // Select a random tile for preview
      const randomIndex = Math.floor(Math.random() * calculatedTiles.length);
      setSelectedTileIndex(randomIndex);
      
      console.log(`Image loaded: ${imageData.metadata.width}x${imageData.metadata.height}`);
      console.log(`Created ${calculatedTiles.length} tiles (${tileSize.width}x${tileSize.height} each)`);
      
    } catch (error) {
      console.error('Error loading file:', error);
      alert(`Error loading file: ${error.message}`);
    }
  };

  // Update preview when tile selection or corrections change
  useEffect(() => {
    if (!tiffUrl || !tiles.length || !previewCanvasRef.current) return;
    
    const updatePreview = async () => {
      const tile = tiles[selectedTileIndex];
      if (!tile) return;
      
      const canvas = previewCanvasRef.current;
      canvas.width = tile.width;
      canvas.height = tile.height;
      const ctx = canvas.getContext('2d');
      
      // Load and draw the tile area
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = tiffUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      ctx.drawImage(
        img,
        tile.x, tile.y, tile.width, tile.height,
        0, 0, tile.width, tile.height
      );
      
      // Apply corrections
      applyAllCorrections(canvas, colorCorrections, transformations);
    };
    
    updatePreview();
  }, [tiffUrl, tiles, selectedTileIndex, colorCorrections, transformations, applyAllCorrections]);

  // Process single tile
  const processTile = async (tile, originalImage) => {
    // Create canvas for the tile
    const canvas = document.createElement('canvas');
    canvas.width = tile.width;
    canvas.height = tile.height;
    const ctx = canvas.getContext('2d');
    
    // Draw tile area
    ctx.drawImage(
      originalImage,
      tile.x, tile.y, tile.width, tile.height,
      0, 0, tile.width, tile.height
    );
    
    // Apply corrections
    applyAllCorrections(canvas, colorCorrections, transformations);
    
    // Convert to blob
    const blob = await new Promise(resolve => {
      canvas.toBlob(resolve, 'image/png');
    });
    
    // Send to AI model
    const formData = new FormData();
    formData.append("image", blob, `tile_${tile.id}.png`);
    formData.append("modelVersion", selectedModel);
    
    const response = await fetch("http://localhost:4000/api/upload", {
      method: "POST",
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to process tile ${tile.id}`);
    }
    
    const data = await response.json();
    
    return {
      tileId: tile.id,
      tile: tile,
      maskUrl: data.predicted
    };
  };

  // Process all tiles
  const processAllTiles = async () => {
    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessedTiles([]);
    
    try {
      // Load original image
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = tiffUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      const processed = [];
      
      // Process tiles in batches to avoid overwhelming the server
      const batchSize = 4;
      for (let i = 0; i < tiles.length; i += batchSize) {
        const batch = tiles.slice(i, i + batchSize);
        
        const batchPromises = batch.map(tile => processTile(tile, img));
        const batchResults = await Promise.all(batchPromises);
        
        processed.push(...batchResults);
        setProcessedTiles(prev => [...prev, ...batchResults]);
        
        const progress = Math.round(((i + batch.length) / tiles.length) * 100);
        setProcessingProgress(progress);
        
        console.log(`Processed ${i + batch.length}/${tiles.length} tiles`);
        
        // Small delay between batches
        if (i + batchSize < tiles.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Merge all masks
      await mergeMasks(processed);
      
    } catch (error) {
      console.error('Error processing tiles:', error);
      alert(`Error processing tiles: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Merge all mask tiles into final mask
  const mergeMasks = async (processedTiles) => {
    console.log('Merging masks...');
    
    // Create final canvas
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = imageDimensions.width;
    finalCanvas.height = imageDimensions.height;
    const ctx = finalCanvas.getContext('2d');
    
    // Fill with black background
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    
    // Load and place each mask tile
    for (const processed of processedTiles) {
      const maskImg = new Image();
      maskImg.crossOrigin = "Anonymous";
      maskImg.src = processed.maskUrl;
      
      await new Promise((resolve) => {
        maskImg.onload = resolve;
      });
      
      // Draw mask tile at correct position
      ctx.drawImage(
        maskImg,
        processed.tile.x,
        processed.tile.y,
        processed.tile.width,
        processed.tile.height
      );
    }
    
    // Convert to blob
    const blob = await new Promise(resolve => {
      finalCanvas.toBlob(resolve, 'image/png');
    });
    
    const url = URL.createObjectURL(blob);
    setFinalMask({ url, blob, canvas: finalCanvas });
    
    console.log('Mask merging complete');
  };

  // Download final mask
  const downloadMask = async () => {
    if (!finalMask) return;
    
    const link = document.createElement('a');
    link.href = finalMask.url;
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const baseName = tiffFile.name.replace(/\.[^/.]+$/, '');
    link.download = `${baseName}_mask_${timestamp}.png`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('Mask downloaded');
  };

  // Download metadata
  const downloadMetadata = () => {
    const metadata = {
      timestamp: new Date().toISOString(),
      originalFile: {
        name: tiffFile.name,
        size: tiffFile.size,
        dimensions: imageDimensions
      },
      processing: {
        tileSize: tileSize,
        totalTiles: tiles.length,
        model: selectedModel,
        colorCorrections: colorCorrections,
        transformations: transformations
      },
      geoLocation: tiffMetadata?.geoTags || null,
      tiles: tiles.map(tile => ({
        id: tile.id,
        position: {
          x: tile.x,
          y: tile.y,
          width: tile.width,
          height: tile.height
        },
        gridPosition: {
          x: tile.gridX,
          y: tile.gridY
        },
        processed: processedTiles.some(p => p.tileId === tile.id)
      }))
    };
    
    const blob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const baseName = tiffFile.name.replace(/\.[^/.]+$/, '');
    link.download = `${baseName}_tile_metadata_${timestamp}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to process TIFF (import from QuadratPreprocessingPage logic)
  const processTiffFile = async (file) => {
    // This would use the same TIFF processing logic from QuadratPreprocessingPage
    // For now, returning a placeholder
    return new Promise((resolve, reject) => {
      if (!window.UTIF) {
        reject(new Error('UTIF library not loaded'));
        return;
      }
      
      const reader = new FileReader();
      reader.onload = function(e) {
        try {
          const buffer = e.target.result;
          const ifds = window.UTIF.decode(buffer);
          
          if (!ifds || ifds.length === 0) {
            throw new Error('No image data found in TIFF file');
          }
          
          const ifd = ifds[0];
          window.UTIF.decodeImage(buffer, ifd);
          const rgba = window.UTIF.toRGBA8(ifd);
          
          const canvas = document.createElement('canvas');
          canvas.width = ifd.width;
          canvas.height = ifd.height;
          const ctx = canvas.getContext('2d');
          
          const imageData = ctx.createImageData(canvas.width, canvas.height);
          imageData.data.set(rgba);
          ctx.putImageData(imageData, 0, 0);
          
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            resolve({
              url,
              metadata: {
                width: ifd.width,
                height: ifd.height,
                geoTags: extractGeoTags(ifd)
              }
            });
          }, 'image/png');
          
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Extract GeoTIFF tags
  const extractGeoTags = (ifd) => {
    const geoTags = {};
    const geoTagNumbers = [33922, 33550, 34735, 34736, 34737];
    
    geoTagNumbers.forEach(tag => {
      const tagKey = `t${tag}`;
      if (ifd[tagKey]) {
        geoTags[tag] = ifd[tagKey];
      }
    });
    
    return Object.keys(geoTags).length > 0 ? geoTags : null;
  };

  // Reset all corrections
  const resetAllCorrections = () => {
    setColorCorrections({
      brightness: 0,
      contrast: 0,
      saturation: 0,
      gamma: 1,
      redEnhancement: 0,
      blueReduction: 0,
      greenBalance: 0,
      depthCompensation: 0,
      turbidityReduction: 0,
      particleReduction: 0,
      colorTemperature: 0,
      tint: 0,
      clarity: 0,
      dehaze: 0
    });
    
    setTransformations({
      rotation: 0,
      scale: 1,
      flipHorizontal: false,
      flipVertical: false
    });
  };

  // Apply underwater presets
  const applyPreset = (presetName) => {
    switch (presetName) {
      case 'shallow':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.3,
          blueReduction: 0.2,
          greenBalance: 0.1,
          depthCompensation: 0.2,
          turbidityReduction: 0.3,
          brightness: 10,
          contrast: 15,
          saturation: 0.2,
          gamma: 1.1,
          clarity: 0.3,
          dehaze: 0.2
        }));
        break;
        
      case 'deep':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.6,
          blueReduction: 0.3,
          greenBalance: 0.2,
          depthCompensation: 0.6,
          turbidityReduction: 0.4,
          brightness: 20,
          contrast: 25,
          saturation: 0.3,
          gamma: 1.3,
          clarity: 0.4,
          dehaze: 0.4,
          particleReduction: 2
        }));
        break;
        
      case 'turbid':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.4,
          blueReduction: 0.1,
          greenBalance: -0.2,
          turbidityReduction: 0.7,
          particleReduction: 4,
          brightness: 15,
          contrast: 30,
          saturation: 0.1,
          gamma: 1.2,
          clarity: 0.6,
          dehaze: 0.6
        }));
        break;
        
      case 'green-water':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.5,
          blueReduction: 0.2,
          greenBalance: -0.4,
          turbidityReduction: 0.5,
          colorTemperature: 0.3,
          tint: -0.3,
          brightness: 10,
          contrast: 20,
          saturation: 0.15,
          gamma: 1.15,
          clarity: 0.4,
          dehaze: 0.3
        }));
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="large-tile-modal">
      <div className="large-tile-content">
        <div className="modal-header">
          <h2>Large Tile Processing</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {!tiffFile ? (
            <div className="upload-section">
              <h3>Upload High-Resolution Image</h3>
              <p>Upload a large TIFF, PNG, or JPEG image for automatic tiling and processing</p>
              <input
                type="file"
                accept=".tiff,.tif,.png,.jpg,.jpeg,image/tiff,image/png,image/jpeg"
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <button 
                className="upload-btn"
                onClick={() => fileInputRef.current.click()}
              >
                📁 Select Image File
              </button>
            </div>
          ) : (
            <div className="processing-section">
              <div className="file-info">
                <h3>File Information</h3>
                <div className="info-grid">
                  <div>📄 File: {tiffFile.name}</div>
                  <div>📐 Dimensions: {imageDimensions?.width} × {imageDimensions?.height}px</div>
                  <div>🔲 Tiles: {tiles.length} ({Math.ceil(imageDimensions?.width / tileSize.width)} × {Math.ceil(imageDimensions?.height / tileSize.height)})</div>
                  <div>📏 Tile Size: {tileSize.width} × {tileSize.height}px</div>
                </div>
              
              {!isProcessing && !finalMask && (
                <div className="action-section">
                  <p className="action-info">
                    Color corrections will be applied to all {tiles.length} tiles before AI processing
                  </p>
                  <button 
                    className="analyze-btn"
                    onClick={processAllTiles}
                  >
                    🤖 Analyze All Tiles
                  </button>
                </div>
              )}
                
                {/* Color Correction Controls */}
                <div className="correction-controls">
                  <h4>Color Corrections</h4>
                  <div className="tabs">
                    <button 
                      className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
                      onClick={() => setActiveTab('basic')}
                    >
                      Basic
                    </button>
                    <button 
                      className={`tab ${activeTab === 'underwater' ? 'active' : ''}`}
                      onClick={() => setActiveTab('underwater')}
                    >
                      Underwater
                    </button>
                    <button 
                      className={`tab ${activeTab === 'advanced' ? 'active' : ''}`}
                      onClick={() => setActiveTab('advanced')}
                    >
                      Advanced
                    </button>
                  </div>
                  
                  <div className="tab-content">
                    {activeTab === 'basic' && (
                      <div>
                        <div className="control-group">
                          <label>Brightness: {colorCorrections.brightness}</label>
                          <input
                            type="range"
                            min="-100"
                            max="100"
                            value={colorCorrections.brightness}
                            onChange={(e) => setColorCorrections(prev => ({...prev, brightness: parseInt(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Contrast: {colorCorrections.contrast}</label>
                          <input
                            type="range"
                            min="-100"
                            max="100"
                            value={colorCorrections.contrast}
                            onChange={(e) => setColorCorrections(prev => ({...prev, contrast: parseInt(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Saturation: {colorCorrections.saturation.toFixed(2)}</label>
                          <input
                            type="range"
                            min="-1"
                            max="1"
                            step="0.1"
                            value={colorCorrections.saturation}
                            onChange={(e) => setColorCorrections(prev => ({...prev, saturation: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Gamma: {colorCorrections.gamma.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.1"
                            value={colorCorrections.gamma}
                            onChange={(e) => setColorCorrections(prev => ({...prev, gamma: parseFloat(e.target.value)}))}
                          />
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'underwater' && (
                      <div>
                        <div className="preset-buttons">
                          <button onClick={() => applyPreset('shallow')}>🏝️ Shallow</button>
                          <button onClick={() => applyPreset('deep')}>🌊 Deep</button>
                          <button onClick={() => applyPreset('turbid')}>🌫️ Turbid</button>
                          <button onClick={() => applyPreset('green-water')}>🌿 Green</button>
                        </div>
                        
                        <div className="control-group">
                          <label>Red Enhancement: {colorCorrections.redEnhancement.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={colorCorrections.redEnhancement}
                            onChange={(e) => setColorCorrections(prev => ({...prev, redEnhancement: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Blue Reduction: {colorCorrections.blueReduction.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={colorCorrections.blueReduction}
                            onChange={(e) => setColorCorrections(prev => ({...prev, blueReduction: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Depth Compensation: {colorCorrections.depthCompensation.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={colorCorrections.depthCompensation}
                            onChange={(e) => setColorCorrections(prev => ({...prev, depthCompensation: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Turbidity Reduction: {colorCorrections.turbidityReduction.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={colorCorrections.turbidityReduction}
                            onChange={(e) => setColorCorrections(prev => ({...prev, turbidityReduction: parseFloat(e.target.value)}))}
                          />
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'advanced' && (
                      <div>
                        <div className="control-group">
                          <label>Color Temperature: {colorCorrections.colorTemperature.toFixed(2)}</label>
                          <input
                            type="range"
                            min="-1"
                            max="1"
                            step="0.1"
                            value={colorCorrections.colorTemperature}
                            onChange={(e) => setColorCorrections(prev => ({...prev, colorTemperature: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Clarity: {colorCorrections.clarity.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={colorCorrections.clarity}
                            onChange={(e) => setColorCorrections(prev => ({...prev, clarity: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Dehaze: {colorCorrections.dehaze.toFixed(2)}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={colorCorrections.dehaze}
                            onChange={(e) => setColorCorrections(prev => ({...prev, dehaze: parseFloat(e.target.value)}))}
                          />
                        </div>
                        
                        <div className="control-group">
                          <label>Particle Reduction: {colorCorrections.particleReduction}</label>
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="1"
                            value={colorCorrections.particleReduction}
                            onChange={(e) => setColorCorrections(prev => ({...prev, particleReduction: parseInt(e.target.value)}))}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button 
                    className="reset-btn" 
                    onClick={resetAllCorrections}
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    🔄 Reset All Corrections
                  </button>
                </div>
              </div>
              
              <div className="preview-section">
                <h3>Preview Tile (#{selectedTileIndex + 1} of {tiles.length})</h3>
                <div className="preview-controls">
                  <button 
                    onClick={() => setSelectedTileIndex(Math.max(0, selectedTileIndex - 1))}
                    disabled={selectedTileIndex === 0}
                  >
                    ← Previous
                  </button>
                  <span>Tile {selectedTileIndex + 1}</span>
                  <button 
                    onClick={() => setSelectedTileIndex(Math.min(tiles.length - 1, selectedTileIndex + 1))}
                    disabled={selectedTileIndex === tiles.length - 1}
                  >
                    Next →
                  </button>
                  <button onClick={() => setSelectedTileIndex(Math.floor(Math.random() * tiles.length))}>
                    🎲 Random
                  </button>
                </div>
                
                <div className="preview-container">
                  <canvas ref={previewCanvasRef} className="preview-canvas" />
                  {tiles[selectedTileIndex] && (
                    <div className="tile-info">
                      Position: ({tiles[selectedTileIndex].x}, {tiles[selectedTileIndex].y})
                    </div>
                  )}
                </div>
              </div>
              
              {isProcessing && (
                <div className="progress-section">
                  <h3>Processing Tiles...</h3>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${processingProgress}%` }}
                    />
                  </div>
                  <p>{processedTiles.length} of {tiles.length} tiles processed ({processingProgress}%)</p>
                </div>
              )}
              
              {finalMask && (
                <div className="results-section">
                  <h3>Processing Complete!</h3>
                  <div className="result-preview">
                    <img src={finalMask.url} alt="Final mask" className="mask-preview" />
                  </div>
                  <div className="download-actions">
                    <button onClick={downloadMask} className="download-btn">
                      💾 Download Mask
                    </button>
                    <button onClick={downloadMetadata} className="metadata-btn">
                      📊 Download Metadata
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LargeTileProcessing;